<template>
  <div class="about-page">
    <!-- Hero -->
    <section class="about-hero">
      <h1 class="about-hero__title">About</h1>
      <p class="about-hero__subtitle">The human and the idea behind Thinking of U</p>
    </section>

    <!-- Ian's story — styled like a letter -->
    <section class="about-section about-section--white">
      <div class="about-container">
        <div class="about-letter reveal-item">
          <h2 class="about-letter__greeting">Hi, I'm Ian</h2>
          <p>
            For years, I've been writing little love notes to my wife — scribbles on notecards,
            silly poems, random doodles. Imperfect, heartfelt, sometimes funny, sometimes profound.
            And somehow, always powerful. They weren't much. But they meant something.
          </p>
          <p>
            One day, just for fun, I asked ChatGPT to help me write a cheeky little message
            about my wife's butt. What it came up with was hilarious, weirdly touching — and
            kinda brilliant. That moment sparked something:
          </p>
        </div>
      </div>
    </section>

    <!-- Spark moment — highlighted quote -->
    <section class="about-section about-section--spark">
      <div class="about-container reveal-item">
        <blockquote class="about-spark">
          <p>
            What if anyone could send a message like that — playful, poetic, surprising —
            without overthinking it? What if technology could help us connect, not just automate things?
          </p>
        </blockquote>
        <p class="about-spark-after">That's how Thinking of U was born.</p>
      </div>
    </section>

    <!-- Gallery break -->
    <section class="about-gallery reveal-item">
      <img
        v-for="(img, i) in galleryImages"
        :key="i"
        :src="img"
        :alt="`Example postcard ${i + 1}`"
        class="about-gallery__img"
        loading="lazy"
      />
    </section>

    <!-- About the product -->
    <section class="about-section about-section--white">
      <div class="about-container">
        <h2 class="about-heading reveal-item">About Thinking of U</h2>

        <div class="about-values">
          <div class="about-value reveal-item" style="transition-delay: 0.1s">
            <h3>Not a postcard builder</h3>
            <p>It's a vibe sender. You choose a few things — the recipient, the tone, the style.
              And then you let go. No previews. No edits. Just a perfectly unique little surprise
              sent through the mail.</p>
          </div>
          <div class="about-value reveal-item" style="transition-delay: 0.2s">
            <h3>Crafted for you, intended by you</h3>
            <p>The message? Generated from scratch. The image? Also one-of-a-kind. The intent? 100% yours.
              And that's the magic.</p>
          </div>
          <div class="about-value reveal-item" style="transition-delay: 0.3s">
            <h3>For everyone you care about</h3>
            <p>Whether it's for your partner, your kid, your best friend, or your weird neighbor
              across the hall — Thinking of U helps you send love, laughs, and connection. One
              unexpected postcard at a time.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Signoff -->
    <section class="about-section about-section--signoff">
      <div class="about-container reveal-item">
        <p class="about-thanks">
          Thanks for being here. I hope this brings a little more warmth into your world.
        </p>
        <p class="about-sig">— Ian</p>
      </div>
    </section>

    <!-- CTA -->
    <section class="about-section about-section--cta">
      <div class="about-container reveal-item">
        <h2 class="about-heading about-heading--light">Send your first surprise</h2>
        <button @click="$router.push('/create-postcard')" class="cta-button cta-button--about">
          Create a Postcard
        </button>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'AboutPage',
  data() {
    return {
      observer: null,
      galleryImages: [
        require('../assets/images/paper_collage.jpeg'),
        require('../assets/images/fever_dream_ducks.jpeg'),
        require('../assets/images/notecard_memory.jpeg'),
        require('../assets/images/papercraft_phoenix.jpeg'),
      ],
    };
  },
  mounted() {
    this.initScrollReveal();
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    initScrollReveal() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('revealed');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );

      this.$nextTick(() => {
        const items = this.$el.querySelectorAll('.reveal-item');
        items.forEach((el) => this.observer.observe(el));
      });
    },
  },
};
</script>

<style scoped>
.about-page {
  background-color: var(--light-pink);
  overflow-x: hidden;
}

/* === Scroll reveal === */
.reveal-item {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.reveal-item.revealed {
  opacity: 1;
  transform: translateY(0);
}

/* === Hero === */
.about-hero {
  padding: 5rem 2rem 3rem;
  text-align: center;
  background: linear-gradient(180deg, var(--light-pink) 0%, white 100%);
}

.about-hero__title {
  font-size: 3rem;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
}

.about-hero__subtitle {
  color: #666;
  font-size: 1.15rem;
}

/* === Sections === */
.about-section {
  padding: 4rem 2rem;
}

.about-section--white {
  background: white;
}

.about-section--spark {
  background: linear-gradient(135deg, var(--rich-black), #1a1a2e);
  padding: 3.5rem 2rem;
}

.about-section--signoff {
  background: var(--light-pink);
  text-align: center;
  padding: 3rem 2rem;
}

.about-section--cta {
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  text-align: center;
  padding: 3.5rem 2rem;
}

.about-container {
  max-width: 780px;
  margin: 0 auto;
}

/* === Letter style === */
.about-letter {
  background: #faf8f5;
  border-radius: 12px;
  padding: 2.5rem 2.5rem 2rem;
  border-left: 4px solid var(--hollywood-cerise);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
}

.about-letter__greeting {
  font-size: 1.8rem;
  color: var(--hollywood-cerise);
  margin-bottom: 1.25rem;
}

.about-letter p {
  font-size: 1.05rem;
  line-height: 1.85;
  color: #444;
  margin-bottom: 1.25rem;
}

.about-letter p:last-child {
  margin-bottom: 0;
}

/* === Spark quote === */
.about-spark {
  border: none;
  margin: 0;
  padding: 0;
  text-align: center;
}

.about-spark p {
  font-size: 1.5rem;
  font-style: italic;
  font-weight: 300;
  color: white;
  line-height: 1.6;
  margin: 0;
}

.about-spark-after {
  text-align: center;
  color: var(--hollywood-cerise);
  font-weight: 700;
  font-size: 1.2rem;
  margin-top: 1.5rem;
}

/* === Gallery strip === */
.about-gallery {
  display: flex;
  gap: 0;
  overflow: hidden;
  height: 200px;
}

.about-gallery__img {
  flex: 1;
  object-fit: cover;
  min-width: 0;
  filter: saturate(0.85);
  transition: filter 0.4s ease;
}

.about-gallery__img:hover {
  filter: saturate(1.1);
}

/* === Headings === */
.about-heading {
  text-align: center;
  margin-bottom: 2rem;
}

.about-heading::after {
  content: '';
  display: block;
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, var(--hollywood-cerise), var(--violet-lush));
  border-radius: 2px;
  margin: 0.5rem auto 0;
}

.about-heading--light {
  color: white;
  margin-bottom: 1.5rem;
}

.about-heading--light::after {
  background: rgba(255, 255, 255, 0.4);
}

/* === Values / product description === */
.about-values {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.about-value {
  padding: 1.5rem;
  background: var(--light-pink);
  border-radius: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.about-value:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
}

.about-value h3 {
  font-size: 1.05rem;
  color: var(--hollywood-cerise);
  margin: 0 0 0.5rem;
}

.about-value p {
  color: #555;
  font-size: 0.98rem;
  line-height: 1.7;
  margin: 0;
}

/* === Signoff === */
.about-thanks {
  font-size: 1.1rem;
  color: #555;
  line-height: 1.7;
  max-width: 500px;
  margin: 0 auto 1rem;
}

.about-sig {
  font-family: 'Caveat', cursive;
  font-size: 2.2rem;
  color: var(--hollywood-cerise);
  margin: 0;
}

/* === CTA button === */
.cta-button--about {
  padding: 16px 40px;
  font-size: 1.1rem;
  font-weight: 600;
  background: white;
  color: var(--hollywood-cerise);
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cta-button--about:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  background: white;
  color: var(--violet-lush);
}

/* === Mobile === */
@media (max-width: 768px) {
  .about-hero__title {
    font-size: 2.2rem;
  }

  .about-spark p {
    font-size: 1.2rem;
  }

  .about-letter {
    padding: 1.5rem;
  }

  .about-gallery {
    height: 140px;
  }
}

/* === Reduced motion === */
@media (prefers-reduced-motion: reduce) {
  .reveal-item {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
